import React from 'react';

import { getZonedDate } from '@eika-infrastruktur/date';
import { Tabs } from '@eika/tabs';

import type { Account } from '../../../generated-types';
import { isValidAccountPaymentFrom, isValidAccountTransferFrom } from '../../../services/accountFilters';
import useFocusContext, {
    FocusContext,
    TransactionDisplayType,
} from '../../NettbankCreateTransactions/hooks/useFocusContext';
import paymentReducer from '../Payment/form/reducer';
import { paymentInitialState } from '../Payment/form/state';
import { PaymentFormContext } from '../Payment/form/useFormContext';
import useValidate from '../Payment/form/validate';
import transferReducer from '../Transfer/form/reducer';
import { transferInitialState } from '../Transfer/form/state';
import { TransferFormContext } from '../Transfer/form/useFormContext';
import * as transferValidate from '../Transfer/form/validate';

import './PaymentAndTransferForm.scss';

type Props = {
    transferForm: React.JSX.Element;
    paymentForm: React.JSX.Element;
    defaultForm?: 'payment' | 'transfer';
    wrapInContext?: ('transfer' | 'payment')[];
    account: Account | undefined;
    isImmediatePaymentFlow?: boolean;
};
type PropsWithFocus = { withFocusContext?: boolean; defaultFocusActive?: boolean } & Props;

export const paymentAndTransferFormSwitchId = 'nettbank-transactions-switch-buttons';

const PaymentAndTransferForm: React.FC<Props> = ({
    defaultForm = 'payment',
    transferForm,
    paymentForm,
    wrapInContext = ['payment', 'transfer'],
    isImmediatePaymentFlow,
    account,
}) => {
    const { transactionType } = useFocusContext();
    return (
        <Tabs
            id={paymentAndTransferFormSwitchId}
            className="payment-and-transfer__tab-wrapper"
            defaultValue={defaultForm}
            onChangeValue={(value) => transactionType.set(value as TransactionDisplayType)}
        >
            {wrapInContext?.includes('payment') ? (
                <PaymentFormContext
                    validateHook={useValidate}
                    reducer={paymentReducer}
                    initialState={{
                        ...paymentInitialState,
                        paymentDate: getZonedDate(),
                        fromAccount: isValidAccountPaymentFrom(account) ? account?.accountNumber : undefined,
                        immediate: isImmediatePaymentFlow,
                    }}
                >
                    <Tabs.Tab value="payment">{paymentForm}</Tabs.Tab>
                </PaymentFormContext>
            ) : (
                <Tabs.Tab value="payment">{paymentForm}</Tabs.Tab>
            )}

            {wrapInContext?.includes('transfer') ? (
                <TransferFormContext
                    validateHook={transferValidate.default}
                    reducer={transferReducer}
                    initialState={{
                        ...transferInitialState,
                        fromAccount: isValidAccountTransferFrom(account) ? account?.accountNumber : undefined,
                    }}
                >
                    <Tabs.Tab value="transfer">{transferForm}</Tabs.Tab>
                </TransferFormContext>
            ) : (
                <Tabs.Tab value="transfer">{transferForm}</Tabs.Tab>
            )}
        </Tabs>
    );
};

export default ({ withFocusContext = true, defaultFocusActive = false, ...props }: PropsWithFocus) =>
    withFocusContext ? (
        <FocusContext defaultActive={defaultFocusActive}>
            <PaymentAndTransferForm {...props} />
        </FocusContext>
    ) : (
        <PaymentAndTransferForm {...props} />
    );
